// Canvas 组件（v0.22）：PDF 页 PNG 底图 + SVG 气球工作台 + 视口缩放/平移
// ------------------------------------------------------------------
// - 气球从后端 GET /page/{n}/balloons 读**持久化**位置（center/anchor/leader），
//   与 PDF 导出同一数据源；拖过的位置会保存，不会每次被自动避让覆盖。
// - **视口变换（v0.22）**：滚轮以鼠标为锚点缩放；按住空白处拖拽平移画布；
//   双击空白复位；控制栏提供 放大/缩小/适应宽度/适应全页/100%。
// - **手势区分**：按在气球上拖 = 移动该气球（leader 实时跟随，松手 PUT 保存）；
//   按在空白（图纸/舞台）上拖 = 平移画布视图。二者互不干扰。
// - **选中定位（v0.22）**：selectedFeaturePk 变化时，若该气球不在可视区内，
//   自动平移（必要时适度放大）使其居中，实现"点表格行/气球 → 跳图纸对应位置"。
// - 点击气球/点表格行双向定位高亮（经 UI store selectedFeaturePk）。
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Feature, PageBalloons, CoverageResult, MissedItem, FeatureStatus, RegionRect } from '../types';
import { getPageBalloons, updateBalloonPos, createFeature, reanalyze, recognizeRegion } from '../api/client';
import { useUI } from '../store/ui';
import { STATUS_COLORS } from '../types';
import type { BalloonStyle } from '../lib/balloonStyle';
import { loadBalloonStyle, saveBalloonStyle, resetBalloonStyle, hasOwnStyle } from '../lib/balloonStyle';
import { blurTypingTarget, isTypingTarget } from '../lib/keys';
import { useFeatureDelete } from '../lib/useFeatureDelete';
import { BalloonStylePanel } from './BalloonStylePanel';

// 前端兜底气泡半径（PDF 点），后端会返回实际半径
const FALLBACK_RADIUS = 11;

// 气泡视觉半径的**屏幕像素**下限：只用于防止极小值时退化成不可见 / 不可点。
// 踩过的坑（2026-09-29）：原值 8 过于保守，而它是**屏幕**像素 —— HiDPI 下再被放大
// 2 倍显示，结果样式面板 scale 只要 < ~1.1 就全部被钳住：拖滑块屏幕纹丝不动，
// 用户反复调小仍「还是太大」。导出侧（pdfout）没有这种地板，纯按 pt 等比缩放，
// 于是画布还比 PDF 更大，违背「所见即所得」。现将地板降到 2.5 屏 px。
// 取值 2.0 是权衡：适应宽度缩放（大图纸约 26%）下，0.3× 仍是「活」的（约 2.25px），
// 只有最低几档才触底；同时大栅格图（短边上千像素）缩到很小时气泡仍可见。
const MIN_BALLOON_PX = 2.0;
// 编号字号相对半径的比例（与导出侧 fs = max(7.0, r*0.78) 的「随半径等比」思路一致）
const BALLOON_FONT_K = 1.1;
const BALLOON_FONT_MIN_PX = 11;
// 视口缩放范围
const ZOOM_MIN = 0.05;
const ZOOM_MAX = 24;

// 键盘快捷键的焦点判断与失焦处理在 lib/keys.ts（App 级 Ctrl+Z 也要用同一份）。
// blurTypingTarget 必须显式调用：画布的 preventDefault() 会把浏览器
// 「点击非聚焦元素即失焦」的默认行为一并挡掉，导致焦点留在详情面板输入框里、
// 后续 Delete 被 isTypingTarget 静默拦下。

interface Props {
  drawingId: number;
  widthPt: number;
  heightPt: number;
  pageIndex: number;
  features: Feature[];
  dpi?: number;           // 渲染 DPI，默认 150
  // 源文件指纹（DrawingSummary.image_version）：拼进底图 URL 做版本参数。
  // 底图 URL 只含 drawingId + pageIndex，而 drawings.id 会被复用 → 必须靠它避开
  // 浏览器 max-age=3600 的强缓存，否则新图纸会显示上一张图纸的底图。
  imageVersion?: string;
  onFeatureClick?: (f: Feature) => void;   // 点击气球时通知父级（联动表格滚动）
  coverage?: CoverageResult | null;        // Task #55：疑似漏检标记源
  toolbarExtra?: React.ReactNode;          // 追加到工具条末尾的内容（如过滤汇总）
  /**
   * v0.56（用户 2026-10-16）：「导出气泡图放到样式旁边」。
   *
   * ★ 为什么单开一个插槽而不是塞进 `toolbarExtra`：
   *   `toolbarExtra` 渲染在**工具条末尾**（`FilterSummary` 过滤汇总占着），
   *   导出按钮放那里会被汇总条隔开、离「样式」很远 —— 而用户要的是**挨着**。
   *   插槽紧贴 `<BalloonStylePanel/>` 之后，位置由本组件锁定，
   *   调用方不必知道工具条的内部顺序（也不必为了插一个按钮去重排 JSX）。
   * ⚠️ 导出按钮本身**不在这里实现**：它要 `drawingId` + 当前页码 + 本地样式偏好，
   *   而这三样在 `App.tsx` 才凑得齐（`Canvas` 只知道页码，不知道导出后要 toast
   *   哪个文件名）。这里只提供位置，避免把导出逻辑复制成两份。
   */
  toolbarAfterStyle?: React.ReactNode;
  /**
   * v0.47：只读角色。关掉画布上全部**写**操作 ——
   * 自动识别 / 手动添加气泡 / 框选识别 / 拖动气球 / Delete 删气泡。
   * 缩放、平移、选中、定位照常（那些是读）。
   * ★ 同样只是界面层；后端 `recognize-region` / `reanalyze` / `balloon` /
   *   `feature` 这些接口都要求 engineer。
   */
  readOnly?: boolean;
}

// 视图状态：zoom = 显示缩放（PNG 像素 → 屏像素），tx/ty = 平移偏移（屏像素）
interface View { zoom: number; tx: number; ty: number; }

export function Canvas({ drawingId, widthPt, heightPt, pageIndex,
                        features, dpi = 150, imageVersion, onFeatureClick, coverage,
                        toolbarExtra, toolbarAfterStyle, readOnly = false }: Props) {
  const ro = readOnly;
  const containerRef = useRef<HTMLDivElement>(null);   // （保留占位，供未来工具栏定位）
  const stageRef = useRef<HTMLDivElement>(null);       // 可视舞台（overflow hidden）
  const qc = useQueryClient();
  const { selectedFeaturePk, selectFeature, setCanvasPage,
          locatedMissed, locatedMissedSeq, setLocatedMissed,
          balloonAddMode, setBalloonAddMode,
          regionSelectMode, setRegionSelectMode, showToast } = useUI();
  const [imgLoaded, setImgLoaded] = useState(false);
  // 真实 PNG 渲染尺寸（naturalWidth/naturalHeight）。后端返回的 PNG 像素宽高，
  // 是画布世界坐标的唯一权威基准（对齐 img 与 overlay，避免 CSS 拉伸失真/模糊）。
  const [natImg, setNatImg] = useState<{ w: number; h: number } | null>(null);
  const [view, setView] = useState<View>({ zoom: 1, tx: 0, ty: 0 });
  // v0.78：放大后叠一层「当前视口」的高清局部图（屏幕绝对定位，不参与世界坐标）。
  //   底图是整页固定 DPI 位图，A0 幅面整页最多只能到 150dpi ≈ 35MP；
  //   再放大就只是把低分辨率图拉长 ⇒ 尺寸文字必然发虚。局部渲染面积只有
  //   视口那么大，可以把 DPI 拉到 400~600 而不爆内存。
  const [sharp, setSharp] = useState<{ url: string; x0: number; y0: number;
                                        x1: number; y1: number } | null>(null);
  const [sharpDpi, setSharpDpi] = useState(0);   // 当前高清层实际 DPI（状态栏显示）
  const sharpSeqRef = useRef(0);
  const [addCount, setAddCount] = useState(0);   // 手动添加气泡：本次连续添加计数
  const [autoBusy, setAutoBusy] = useState(false); // 自动识别进行中
  // 气泡显示样式（**按图纸**存，v0.77）
  // ★ 旧实现是 `useState(loadBalloonStyle)` 一次性读全局键 ⇒ 在图纸 A 调完，
  //   切到图纸 B 还是同一份值（用户 2026-10-19 报的「怎么全部应用了」）。
  //   现在初值与切图都由 drawingId 决定，并在 effect 里重读。
  const [bs, setBs] = useState<BalloonStyle>(() => loadBalloonStyle(drawingId));
  const [bsOwn, setBsOwn] = useState<boolean>(() => hasOwnStyle(drawingId));
  useEffect(() => {
    setBs(loadBalloonStyle(drawingId));
    setBsOwn(hasOwnStyle(drawingId));
  }, [drawingId]);
  const changeBs = (s: BalloonStyle) => {
    setBs(s);
    saveBalloonStyle(s, drawingId);     // 只写这张图纸的键
    setBsOwn(true);
  };
  const resetBs = () => {
    const d = resetBalloonStyle(drawingId);   // 复位到出厂默认（写本图纸的键）
    setBs(d);
    setBsOwn(true);
  };
  const viewRef = useRef(view); viewRef.current = view;
  const [stageSize, setStageSize] = useState({ w: 800, h: 600 });
  // 上次已完成"选中居中定位"的特征 pk（ref，避免重渲染）；markViewTouched 会清空以允许再次定位
  const lastAppliedPkRef = useRef<number | null>(null);
  // Task #55：已处理过的"疑似漏检定位"序列号，避免重复居中
  const handledMissedSeqRef = useRef<number>(-1);
  // 切图 effect 引用最新 fitView（fitView 是 useCallback，定义在下面；用 ref 解耦顺序）
  const fitViewRef = useRef<((m: 'width' | 'page' | 'full') => void) | null>(null);
  // 抑制下一次"选中居中"：拖动开始时设 true（拖动触发的选中不应再把视图跳一下），
  // 松手后下一轮 effect 跑完再清掉，避免 onBalloonPointerUp 末尾 setDragPk(null)
  // 触发 effect 重跑时把视图拉走产生"图纸弹跳"。
  const suppressLocateRef = useRef(false);

  // 拖动状态（区分：'balloon' 移动气球 | 'pan' 平移画布 | 'region' 框选区域）
  const [dragPk, setDragPk] = useState<number | null>(null);
  const [dragCenter, setDragCenter] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{
    mode: 'balloon' | 'pan' | 'region';
    pk: number | null;
    startClientX: number; startClientY: number;
    startCenterX: number; startCenterY: number;
    startTx: number; startTy: number;
    moved: boolean;
  } | null>(null);

  // ---------- 长按空格 = 临时抓手（CAD 式临时平移） ----------
  // 按住空格期间：鼠标指针变手掌，拖拽任意位置（含气球、含框选模式下）都平移图纸，
  // 不会开始框选、也不会移动气球。松开空格立即恢复原模式。
  // 用 ref 同步记录，pointerdown 是原生事件回调，读 state 会拿到旧值。
  const [spaceDown, setSpaceDown] = useState(false);
  const spaceDownRef = useRef(false);

  // ---------- 框选识别（品质大师式） ----------
  // selRegion 以**页面坐标**存储（PDF=pt / 栅格图=原始像素），随缩放平移自动贴图
  const [selRegion, setSelRegion] = useState<RegionRect | null>(null);
  const [regionDraft, setRegionDraft] = useState<RegionRect | null>(null);
  // 草稿同步存 ref：pointerup 里需要读取最新值（state 更新是异步的）
  const regionDraftRef = useRef<RegionRect | null>(null);
  const setDraft = useCallback((d: RegionRect | null) => {
    regionDraftRef.current = d;
    setRegionDraft(d);
  }, []);
  const [regionBusy, setRegionBusy] = useState(false);
  // 框选识别 = 只识别选区内尺寸并**追加**气泡（不清空重建），与「自动识别」不同
  const regionMut = useMutation({
    mutationFn: async (rect: RegionRect) => {
      setRegionBusy(true);
      try { return await recognizeRegion(drawingId, pageIndex, rect); }
      finally { setRegionBusy(false); }
    },
    onSuccess: (res) => {
      const extra = res.skipped > 0 ? `，已跳过 ${res.skipped} 个重复标注` : '';
      if (res.added > 0) {
        showToast(`框选识别完成：新增 ${res.added} 个特征（识别到 ${res.recognized} 个）${extra}`, 'success');
      } else if (res.recognized > 0) {
        showToast(`框选识别：识别到 ${res.recognized} 个，均已在图纸上标注过${extra}`, 'info');
      } else {
        showToast('框选识别：该区域未识别到尺寸', 'info');
      }
      setSelRegion(null);
      qc.invalidateQueries({ queryKey: ['drawing', drawingId] });
      qc.invalidateQueries({ queryKey: ['drawings'] });
      qc.invalidateQueries({ queryKey: ['balloons', drawingId, pageIndex] });
    },
    onError: (e: any) => {
      showToast(`框选识别失败：${e.message}`, 'error');
      setSelRegion(null);
    },
  });

  const toggleRegionMode = () => {
    const next = !regionSelectMode;
    setRegionSelectMode(next);
    setSelRegion(null);
    setRegionDraft(null);
  };

  const balloonsQ = useQuery<PageBalloons>({
    queryKey: ['balloons', drawingId, pageIndex],
    queryFn: () => getPageBalloons(drawingId, pageIndex),
    staleTime: 0,   // 切图返回始终拉最新，避免陈旧缓存导致气泡不显示
  });

  // 保存拖动位置（PUT，不改变状态）
  const saveMut = useMutation({
    mutationFn: (patch: { featurePk: number; center_x: number; center_y: number;
                          leader_points: number[][] }) =>
      updateBalloonPos(drawingId, patch.featurePk, {
        center_x: patch.center_x, center_y: patch.center_y,
        leader_points: patch.leader_points,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['balloons', drawingId, pageIndex] });
      qc.invalidateQueries({ queryKey: ['drawing', drawingId] }); // 反映 center 持久化
    },
    onError: (e: any) => console.error('保存气球失败', e),
  });

  // 手动添加气泡（v1 能力回归）：在画布空白处点击放置一个新特征
  const addMut = useMutation({
    mutationFn: (p: { x: number; y: number; page_no: number }) =>
      createFeature(drawingId, p),
    onSuccess: (f) => {
      setAddCount(c => c + 1);
      showToast(`已添加气泡 #${f.balloon_no}`, 'success');
      qc.invalidateQueries({ queryKey: ['drawing', drawingId] });
      qc.invalidateQueries({ queryKey: ['drawings'] });
      qc.invalidateQueries({ queryKey: ['balloons', drawingId, pageIndex] });
      selectFeature(f.id);   // 选中新特征 → 弹出详情面板供填写尺寸
    },
    onError: (e: any) => showToast(`添加失败：${e.message}`, 'error'),
  });

  // 切换手动添加模式：开启时清空计数
  const toggleAddMode = () => {
    const next = !balloonAddMode;
    setBalloonAddMode(next);
    if (next) setAddCount(0);
  };

  // 自动识别：对当前图纸重跑 AI 分析并自动生成气泡（会重建本图特征，
  // 手动添加的气泡会被覆盖/重建，故执行前需确认）。与手动添加互斥。
  const autoMut = useMutation({
    mutationFn: async () => {
      setAutoBusy(true);
      try { return await reanalyze(drawingId, {}); }
      finally { setAutoBusy(false); }
    },
    onSuccess: (res) => {
      setBalloonAddMode(false);          // 互斥：识别完成后退出手动添加态
      selectFeature(null);
      showToast(`自动识别完成：${res.features.length} 个特征`, 'success');
      qc.invalidateQueries({ queryKey: ['drawing', drawingId] });
      qc.invalidateQueries({ queryKey: ['drawings'] });
      qc.invalidateQueries({ queryKey: ['balloons', drawingId, pageIndex] });
    },
    onError: (e: any) => showToast(`自动识别失败：${e.message}`, 'error'),
  });
  const runAutoRecognize = () => {
    if (autoBusy) return;
    if (!window.confirm(
      '自动识别将重新分析本图并按 AI 结果重建全部气泡，会覆盖/重建当前已确认或手动添加的气泡。\n\n' +
      '如果你只是想补识别某个区域，请改用「⬚ 框选识别」（只追加、不重建）。\n\n' +
      '确定继续吗？')) return;
    setBalloonAddMode(false);            // 互斥：点自动识别即退出手动态
    setRegionSelectMode(false);          // 互斥：同时退出框选态
    setSelRegion(null);
    autoMut.mutate();
  };

  // ---------- Delete 键删除选中气球（v0.26） ----------
  // 以前删掉一个误检气泡要走三步：右侧特征表里找到那一行 → 点「删除」→ 点确定。
  // 可气泡本来就是从画布上看出来的，所以这里让画布自己接住 Delete：
  // 点一下气球选中，按 Delete 键即可删除。
  // 查的是 features（全图）而非 pageFeatures：选中项可能属于别的页
  //（比如从表格点行选中），行为与表格上的「删除」按钮保持一致。
  //
  // 2026-09-16：按用户要求**去掉二次确认**（按了就删）。兜底改为删除后提示条上的
  // 「撤销」按钮 + 全局 Ctrl+Z —— 后端的撤销栈能把特征放回原编号位置。
  const deleteTarget = useMemo(
    () => (selectedFeaturePk == null
      ? null
      : features.find(x => x.id === selectedFeaturePk) ?? null),
    [features, selectedFeaturePk]);
  // 目标存进 ref，下面的 window 监听就只需装一次、也不会拿到失效的闭包
  const deleteTargetRef = useRef<Feature | null>(null);
  deleteTargetRef.current = deleteTarget;

  const { remove: mutateDelete } = useFeatureDelete(
    drawingId,
    // 删掉的正好是当前选中项 → 收起详情面板
    (pk) => { if (selectedFeaturePk === pk) selectFeature(null); });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Z 撤销由 App 统一处理（全局一份）—— 这里再装一次会撤销两次。
      // Delete 是主键，Backspace 一并接住：笔记本键盘常常只有退格键
      //（Mac 上那颗印着 delete 的键实际发 Backspace），只认 Delete 会对一半人失效。
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (e.repeat) return;                  // 长按连发，别一次删掉一串
      if (isTypingTarget(e.target)) return;  // 输入框里的退格是删字符，不是删气泡
      if (ro) return;                        // v0.47：只读角色不删气泡
      const f = deleteTargetRef.current;
      if (!f) return;                        // 没选中任何气泡 → 不拦，交回浏览器默认行为
      e.preventDefault();                    // 拦住 Backspace 的「后退到上一页」
      mutateDelete(f.id);                    // 直接删（不弹确认框），可按 Ctrl+Z 或点提示条撤销
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mutateDelete, ro]);

  // 切换页码/图纸时重置图片加载态，避免旧页底图滞留、overlay 误叠加。
  // imageVersion 也进依赖：源文件被替换（id 复用 / 重新上传）时同样要重置。
  useEffect(() => {
    setImgLoaded(false);
    setNatImg(null);   // 图尺寸失效 → 几何回退到估算，直到新 PNG 加载
  }, [pageIndex, drawingId, imageVersion]);

  // 切图纸时清空手动添加计数（绘制中的计数仅对当前图纸有意义）
  useEffect(() => { setAddCount(0); }, [drawingId]);

  // 长按空格 → 临时抓手（window 级监听，指针在画布外也能生效）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (isTypingTarget(e.target)) return;   // 输入框内空格是正常字符，不劫持
      e.preventDefault();               // 阻止页面滚动 / 避免空格激活获得焦点的按钮
      if (spaceDownRef.current) return; // 长按会连发 keydown，去抖
      spaceDownRef.current = true;
      setSpaceDown(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      spaceDownRef.current = false;
      setSpaceDown(false);
    };
    // 兜底：窗口失焦（切 Tab / 弹窗夺焦）时 keyup 会丢，否则回到页面后鼠标一直停在抓手态
    const onBlur = () => { spaceDownRef.current = false; setSpaceDown(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // 退出框选模式（或切图/切页）时丢弃未确认的选区，避免误识别到别的图
  useEffect(() => {
    if (!regionSelectMode) { setSelRegion(null); setDraft(null); }
  }, [regionSelectMode, drawingId, pageIndex, setDraft]);

  // 监听可视舞台尺寸（viewport）
  useEffect(() => {
    if (!stageRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr && (cr.width > 0 && cr.height > 0)) {
        setStageSize({ w: cr.width, h: cr.height });
      }
    });
    ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);

  // PNG 像素尺寸（世界坐标基准）。图片加载后以 natural 尺寸为准；
  // 未加载前按 widthPt×dpi/72 估算（保持与旧行为一致，避免首次闪烁）。
  const natReady = natImg && natImg.w > 0 && widthPt > 0;
  const pngW = natReady ? natImg.w : (widthPt * dpi) / 72;
  const pngH = natReady ? natImg.h : (heightPt * dpi) / 72;
  // 绘图单位 → 世界(PNG)像素 换算系数：加载后 = naturalWidth/widthPt（真实比例），
  // 未加载 = dpi/72（估算）。对 PDF（widthPt=pt）二者等价；对 TIF（widthPt=像素，
  // 无物理 dpi）用 natural 比例保证 1:1 不超采样、不模糊。
  const unitToPx = natReady ? (natImg.w / widthPt) : (dpi / 72);

  const balloonRadius = balloonsQ.data?.radius ?? FALLBACK_RADIUS;
  const r = balloonRadius;

  // ---------- v0.78 高清局部层：按当前缩放决定要不要拉局部高 DPI 图 ----------
  // 判据：屏幕上每英寸的像素数（needDpi = 缩放后每 pt 占几屏像素 × 72）
  // 超过底图本身 DPI 的 1.25 倍，就说明"已经在把底图拉长"了。
  useEffect(() => {
    if (!imgLoaded || stageSize.w <= 0 || stageSize.h <= 0) return;
    if (widthPt <= 0 || heightPt <= 0) return;
    const seq = ++sharpSeqRef.current;
    const baseDpi = (natReady ? natImg!.w / widthPt : dpi / 72) * 72;
    const spp = view.zoom * unitToPx;          // 屏像素 / pt
    const needDpi = spp * 72;                  // 屏幕实际需要的渲染 DPI
    if (needDpi < baseDpi * 1.25 || needDpi < 170) {
      setSharp(null); setSharpDpi(0);
      return;
    }
    const target = Math.min(600, Math.max(200, Math.round(needDpi * 1.15 / 50) * 50));
    const timer = window.setTimeout(() => {
      const padPx = 0.08;                       // 视口四周多要 8%，平移时不立刻缺边
      const vwPt = stageSize.w / spp;
      const vhPt = stageSize.h / spp;
      let x0 = (-view.tx) / spp - vwPt * padPx;
      let y0 = (-view.ty) / spp - vhPt * padPx;
      let x1 = (-view.tx) / spp + vwPt * (1 + padPx);
      let y1 = (-view.ty) / spp + vhPt * (1 + padPx);
      x0 = Math.max(0, x0); y0 = Math.max(0, y0);
      x1 = Math.min(widthPt, x1); y1 = Math.min(heightPt, y1);
      if (x1 <= x0 || y1 <= y0) return;
      // 与后端同一口径的像素上限，避免请求被拒
      const area = (x1 - x0) * (y1 - y0);
      const capDpi = Math.floor(Math.sqrt(36e6 * 5184 / Math.max(1, area)));
      const useDpi = Math.max(120, Math.min(target, capDpi));
      const q = `x0=${x0.toFixed(1)}&y0=${y0.toFixed(1)}` +
                `&x1=${x1.toFixed(1)}&y1=${y1.toFixed(1)}&dpi=${useDpi}`;
      const url = `/api/drawing/${drawingId}/page/${pageIndex}/clip?${q}` +
                  `&v=${imageVersion || '0'}`;
      const im = new Image();
      im.onload = () => {
        if (sharpSeqRef.current !== seq) return;   // 已被更新的请求取代
        setSharp({ url, x0, y0, x1, y1 });
        setSharpDpi(useDpi);
      };
      // 高清层失败绝不能影响底图 —— 静默退回整页位图
      im.onerror = () => { /* noop */ };
      im.src = url;
    }, 220);
    return () => window.clearTimeout(timer);
  }, [imgLoaded, stageSize.w, stageSize.h, view.zoom, view.tx, view.ty,
      unitToPx, natReady, natImg, widthPt, heightPt, drawingId, pageIndex,
      imageVersion, dpi]);

  // 切图 / 切页：立刻丢掉上一张图的高清层（否则会拿旧区域盖在新图上）
  useEffect(() => { setSharp(null); setSharpDpi(0); }, [drawingId, pageIndex]);

  const pageFeatures = useMemo(
    () => features.filter(f => (f.page_no ?? f.page ?? 0) === pageIndex),
    [features, pageIndex]);

  // Task #55：当前页的疑似漏检标记（仅渲染本页；点击可定位/高亮）
  const missedOnPage = useMemo(
    () => (coverage?.items ?? []).filter(i => i.page_no === pageIndex),
    [coverage, pageIndex]);

  // pk -> 持久化气球 {center, anchor, leader}
  const layoutMap = useMemo(() => {
    const m = new Map<number, {
      center: { x: number; y: number };
      anchor: { x: number; y: number };
      leader: number[][];
    }>();
    for (const b of balloonsQ.data?.balloons ?? []) {
      if (b.feature_pk != null) {
        m.set(b.feature_pk, {
          center: { x: b.center_x ?? b.x, y: b.center_y ?? b.y },
          anchor: { x: b.anchor_x, y: b.anchor_y },
          leader: b.leader_points ?? [],
        });
      }
    }
    return m;
  }, [balloonsQ.data]);

  // 该特征当前气球中心（PDF 点坐标；绘制时统一 ×(dpi/72) 转 PNG 像素）
  const balloonCenterPt = useCallback((f: Feature): { x: number; y: number } => {
    if (dragPk === f.id && dragCenter) return dragCenter;
    const lp = layoutMap.get(f.id);
    if (lp) return lp.center;
    return { x: f.x + r, y: f.y - r };
  }, [dragPk, dragCenter, layoutMap, r]);

  // ---------- 视图计算工具 ----------

  // 适应：可选 fit 模式
  const fitView = useCallback((mode: 'width' | 'page' | 'full') => {
    if (stageSize.w <= 0 || stageSize.h <= 0) return;
    let z = 1;
    if (mode === 'full') {
      // 适配全页（尽量放大且不裁剪，图完整可见）
      z = Math.min(stageSize.w / pngW, stageSize.h / pngH);
    } else {
      z = stageSize.w / pngW;  // 适配宽度
    }
    z = Math.min(Math.max(z, ZOOM_MIN), ZOOM_MAX);
    lastAppliedPkRef.current = null; // 视图被重置，允许下次选中再次定位
    setView({
      zoom: z,
      tx: (stageSize.w - pngW * z) / 2,
      ty: (stageSize.h - pngH * z) / 2,
    });
  }, [stageSize.w, stageSize.h, pngW, pngH]);

  // 让切图 effect 能引用最新 fitView（其 useCallback 在下方定义）
  useEffect(() => { fitViewRef.current = fitView; }, [fitView]);

  // 首次加载 / 切换页码 / 舞台尺寸首到：适配宽度
  const fitAppliedRef = useRef(false);
  useEffect(() => {
    if (!imgLoaded || stageSize.w <= 0) return;
    if (!fitAppliedRef.current) {
      fitAppliedRef.current = true;
      fitView('width');
    }
  }, [imgLoaded, stageSize.w, fitView]);

  // 页码或 drawing 切换 → 重新适配
  // ⚠️ 关键：lastKeyRef 必须按"drawingId 是否变化"重置。否则：
  //   A 图 → B 图 → A 图时，最后回到 A 的 key 与最初一致，useEffect 不会触发，
  //   fitAppliedRef 也不会被复位 → view 仍保持 B 图时的偏移/缩放，气泡全部跑出可视区。
  //   切回后只能"点特征表某一行"触发 selectFeature → 居中 effect 才把视图拉回来。
  const lastKeyRef = useRef<string>('');
  const lastDrawingIdRef = useRef<number>(-1);
  const key = `${drawingId}-${pageIndex}`;
  useEffect(() => {
    if (lastDrawingIdRef.current !== drawingId) {
      // 切图：完整重置视图到"适配宽度"待执行态，清掉所有持久化 ref
      lastDrawingIdRef.current = drawingId;
      lastKeyRef.current = '';          // 让下方 key effect 必然重跑
      fitAppliedRef.current = false;
      lastAppliedPkRef.current = null;  // 切图后允许下次选中再次居中
      handledMissedSeqRef.current = -1;
      // 立即用新图尺寸 + 当前 stage 适配一次（不等 imgLoaded），
      // 避免新 PNG 加载途中出现"老 view + 新图" 错位空白
      if (stageSize.w > 0) {
        fitViewRef.current?.('width');
      } else {
        setView({ zoom: 1, tx: 0, ty: 0 });
      }
    }
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      fitAppliedRef.current = false;
    }
  }, [key, drawingId]);

  // ---------- 选中定位：表行/气球点击 → 图纸对应位置居中（v0.22） ----------
  // 当 selectedFeaturePk 变化且该特征属于当前页时，把该气球平移到视口中央。
  // - 任何缩放/平移状态下点表格行都会"跳"到对应气泡，不再依赖"是否在视口内"，
  //   修复 v0.23 反馈：图纸放大后点特征栏行不跳转。
  // - 仅"用户意图切换选择"时触发；气球本身被拖动时由 dragPk 抑制；
  //   拖动触发的选中由 suppressLocateRef 抑制，避免拖动/松手瞬间把视图跳一下。
  useEffect(() => {
    if (selectedFeaturePk == null) return;
    if (lastAppliedPkRef.current === selectedFeaturePk) return; // 同一 pk 已定位过
    const f = pageFeatures.find(x => x.id === selectedFeaturePk);
    if (!f) { lastAppliedPkRef.current = selectedFeaturePk; return; } // 不在本页，不定位
    if (dragPk != null) return; // 正在拖气球，不抢视图
    if (suppressLocateRef.current) {
      // 拖动引起的选中：标记已处理（避免后续 effect 重跑再触发居中），
      // 但不动视图。下一次用户主动点击/点表格行切换选择时由 suppressLocateRef 清空。
      lastAppliedPkRef.current = selectedFeaturePk;
      return;
    }
    // balloonCenterPt 返回 PDF 点；绘图/定位均以"世界(PNG 像素)"为基准，先 ×(dpi/72)。
    const c = balloonCenterPt(f);
    setView((v) => {
      let targetZoom = v.zoom;
      // 缩放过小时适度放大到可读（< 0.55 才放大，避免在已放大的图上被强行缩小）
      if (v.zoom < 0.55) {
        targetZoom = Math.min(1.0, pngW > 0 ? (stageSize.w / pngW) * 2.2 : 1);
      }
      // c 为 PDF 点 → ×(dpi/72) 转 PNG 像素（世界坐标）后平移居中
      const wxc = c.x * unitToPx;
      const wyc = c.y * unitToPx;
      const cx = stageSize.w / 2 - wxc * targetZoom;
      const cy = stageSize.h / 2 - wyc * targetZoom;
      return { zoom: targetZoom, tx: cx, ty: cy };
    });
    lastAppliedPkRef.current = selectedFeaturePk;
  }, [selectedFeaturePk, pageFeatures, dragPk, balloonCenterPt,
      stageSize.w, stageSize.h, pngW, unitToPx]);

  // ---------- Task #55：疑似漏检定位 — 点击徽章/标记 → 居中到漏检坐标 ----------
  // 与选中特征居中逻辑对称；用 locatedMissedSeq 驱动重复居中（同项可多次定位）。
  useEffect(() => {
    if (!locatedMissed) return;
    // 1) 若不在当前页，先切页（切页后 effect 重跑，本帧不居中）
    if (locatedMissed.page_no !== pageIndex) {
      setCanvasPage(locatedMissed.page_no);
      return;
    }
    // 2) 已处理过同一序列则跳过（避免切页/缩放等无关更新重复居中）
    if (locatedMissedSeq === handledMissedSeqRef.current) return;
    handledMissedSeqRef.current = locatedMissedSeq;
    // 3) 居中：cx/cy 为 PDF 点 → ×(dpi/72) 转 PNG 像素世界坐标
    setView((v) => {
      let targetZoom = v.zoom;
      if (v.zoom < 0.55) {
        targetZoom = Math.min(1.0, pngW > 0 ? (stageSize.w / pngW) * 2.2 : 1);
      }
      const wxc = locatedMissed.x * unitToPx;
      const wyc = locatedMissed.y * unitToPx;
      return {
        zoom: targetZoom,
        tx: stageSize.w / 2 - wxc * targetZoom,
        ty: stageSize.h / 2 - wyc * targetZoom,
      };
    });
    lastAppliedPkRef.current = null; // 允许下一次选中特征再次居中
  }, [locatedMissed, locatedMissedSeq, pageIndex,
      stageSize.w, stageSize.h, pngW, unitToPx, setCanvasPage]);

  // 用户手动缩放/平移时，重置 lastApplied，使下一次选中可再次定位
  const markViewTouched = useCallback(() => { lastAppliedPkRef.current = null; }, []);

  // ---------- 滚轮缩放（以光标为锚点） ----------
  // 用原生 non-passive 监听器挂到 stage，避免 React passive wheel 的 preventDefault 失效导致页面同时滚动
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      if (dragRef.current) return;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.2 : 1 / 1.2;
      const old = viewRef.current;
      const newZoom = Math.min(Math.max(old.zoom * factor, ZOOM_MIN), ZOOM_MAX);
      if (newZoom === old.zoom) return;
      // 保持光标处世界点不动
      const wx = (mx - old.tx) / old.zoom;
      const wy = (my - old.ty) / old.zoom;
      setView({ zoom: newZoom, tx: mx - wx * newZoom, ty: my - wy * newZoom });
      markViewTouchedRef.current?.();
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // 让原生 wheel handler 能调用 markViewTouched（useEffect 在 markViewTouched 之前）
  const markViewTouchedRef = useRef<(() => void) | null>(null);
  useEffect(() => { markViewTouchedRef.current = markViewTouched; }, [markViewTouched]);

  // 双击空白复位到适配宽度
  const onDoubleClick = useCallback((e: React.MouseEvent) => {
    // 仅空白处（舞台背景），不打到气球上（气球上由 g 处理 stopPropagation）
    fitView('width');
    markViewTouched();
  }, [fitView, markViewTouched]);

  // ---------- 指针事件：区分气球拖动 / 画布平移 / 框选 ----------
  // 屏幕坐标（相对舞台）→ 页面坐标（PDF pt / 栅格图像素），与 Feature.x/y 同源
  const screenToPage = (mx: number, my: number) => {
    const ptPerPx = unitToPx * viewRef.current.zoom;
    return {
      x: (mx - viewRef.current.tx) / ptPerPx,
      y: (my - viewRef.current.ty) / ptPerPx,
    };
  };

  const onStagePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.button !== 1) return; // 左键或中键
    const target = e.target as Element;
    // ★ 长按空格 = 临时抓手：优先级高于落点 / 框选 / 拖气球 ——
    //   按住空格后在任何位置（包括气球上、框选模式下）拖动都是平移图纸。
    if (spaceDownRef.current) {
      e.preventDefault();
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      dragRef.current = {
        mode: 'pan', pk: null,
        startClientX: e.clientX, startClientY: e.clientY,
        startCenterX: 0, startCenterY: 0,
        startTx: viewRef.current.tx, startTy: viewRef.current.ty,
        moved: false,
      };
      return;
    }
    // 手动添加气泡模式：空白处点击放置（落在已有气球/漏检标记上则忽略）
    if (balloonAddMode) {
      if (target.closest('.balloon') || target.closest('.missed-marker')) return;
      e.preventDefault();
      const rect = stageRef.current!.getBoundingClientRect();
      const p = screenToPage(e.clientX - rect.left, e.clientY - rect.top);
      if (p.x < 0 || p.y < 0 || p.x > widthPt || p.y > heightPt) return;
      addMut.mutate({
        x: Math.round(p.x * 100) / 100,
        y: Math.round(p.y * 100) / 100,
        page_no: pageIndex,
      });
      return;
    }
    // 框选识别模式：拖拽画矩形选区（不落点、不平移视图）
    if (regionSelectMode) {
      if (regionBusy) return;   // 识别进行中：忽略新的框选，避免请求叠加
      if (target.closest('.balloon') || target.closest('.missed-marker')) return;
      e.preventDefault();
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      const rect = stageRef.current!.getBoundingClientRect();
      const p = screenToPage(e.clientX - rect.left, e.clientY - rect.top);
      setSelRegion(null);
      setDraft({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      dragRef.current = {
        mode: 'region', pk: null,
        startClientX: e.clientX, startClientY: e.clientY,
        startCenterX: p.x, startCenterY: p.y,
        startTx: viewRef.current.tx, startTy: viewRef.current.ty,
        moved: false,
      };
      return;
    }
    // 只有事件源自舞台/底图（未落在气球上）才进入平移
    if (target.closest('.balloon')) return; // 气球由自身 onPointerDown 处理
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      mode: 'pan', pk: null,
      startClientX: e.clientX, startClientY: e.clientY,
      startCenterX: 0, startCenterY: 0,
      startTx: viewRef.current.tx, startTy: viewRef.current.ty,
      moved: false,
    };
  };

  const onStagePointerMove = (e: React.PointerEvent) => {
    const dr = dragRef.current;
    if (!dr) return;
    if (dr.mode === 'region') {
      const rect = stageRef.current!.getBoundingClientRect();
      const p = screenToPage(e.clientX - rect.left, e.clientY - rect.top);
      dr.moved = true;
      setDraft({ x0: dr.startCenterX, y0: dr.startCenterY, x1: p.x, y1: p.y });
      return;
    }
    if (dr.mode === 'pan') {
      const dx = e.clientX - dr.startClientX;
      const dy = e.clientY - dr.startClientY;
      if (!dr.moved && Math.abs(dx) + Math.abs(dy) > 2) dr.moved = true;
      if (dr.moved) {
        setView({ ...viewRef.current, tx: dr.startTx + dx, ty: dr.startTy + dy });
        markViewTouched();
      }
    }
  };

  const onStagePointerUp = () => {
    const dr = dragRef.current;
    if (dr?.mode === 'region') {
      // 把草稿固化为正式选区（规整方向；屏幕尺寸过小则视为误触丢弃）
      const d = regionDraftRef.current;
      if (d) {
        const r: RegionRect = {
          x0: Math.min(d.x0, d.x1), y0: Math.min(d.y0, d.y1),
          x1: Math.max(d.x0, d.x1), y1: Math.max(d.y0, d.y1),
        };
        const perPx = unitToPx * viewRef.current.zoom;   // 页面单位 / 屏幕像素
        const minSidePage = perPx > 0 ? 10 / perPx : 0;   // 至少 10 屏幕像素
        if (Math.min(r.x1 - r.x0, r.y1 - r.y0) >= Math.max(minSidePage, 2)) {
          setSelRegion(r);
          // 松手即自动识别：不再需要额外点确认按钮（重框一次即覆盖选区）
          regionMut.mutate(r);
        }
      }
      setDraft(null);
    }
    dragRef.current = null;
  };

  const onBalloonPointerDown = (f: Feature, e: React.PointerEvent) => {
    // 空格抓手态：不拦截、不 stopPropagation —— 让事件冒泡到舞台，由舞台统一平移图纸
    if (spaceDownRef.current) return;
    if (e.button !== 0) return;
    // v0.47：只读角色**仍可点选**（选中 → 表格联动是读操作），但不能拖动气球。
    if (ro) {
      selectFeature(f.id);
      onFeatureClick?.(f);
      return;
    }
    blurTypingTarget();   // 见该函数注释：preventDefault 会吞掉默认失焦
    e.preventDefault();
    e.stopPropagation();
    const lp = layoutMap.get(f.id);
    const base = lp ? lp.center : { x: f.x + r, y: f.y - r };
    // 拖动引起的选中不应触发"居中定位"——会在拖动/松手瞬间把视图跳一下。
    // 标志位在 onBalloonPointerUp 末尾下一轮 effect 跑完后清掉。
    suppressLocateRef.current = true;
    selectFeature(f.id);
    onFeatureClick?.(f);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      mode: 'balloon', pk: f.id,
      startClientX: e.clientX, startClientY: e.clientY,
      startCenterX: base.x, startCenterY: base.y,
      startTx: viewRef.current.tx, startTy: viewRef.current.ty,
      moved: false,
    };
    setDragPk(f.id);
    setDragCenter({ x: base.x, y: base.y });
  };

  const onBalloonPointerMove = (f: Feature, e: React.PointerEvent) => {
    const dr = dragRef.current;
    if (!dr || dr.mode !== 'balloon') return;
    // 屏幕像素增量 → PDF 点增量：除 zoom（PNG px→屏px）再除 (dpi/72)（PNG px→PDF 点）
    const pxPerPt = viewRef.current.zoom * unitToPx;
    const dxPt = (e.clientX - dr.startClientX) / pxPerPt;
    const dyPt = (e.clientY - dr.startClientY) / pxPerPt;
    const nx = Math.min(Math.max(2, dr.startCenterX + dxPt), widthPt - 2);
    const ny = Math.min(Math.max(2, dr.startCenterY + dyPt), heightPt - 2);
    if (!dr.moved && Math.abs(e.clientX - dr.startClientX) + Math.abs(e.clientY - dr.startClientY) > 2) {
      dr.moved = true;
    }
    setDragCenter({ x: nx, y: ny });
  };

  const onBalloonPointerUp = () => {
    // 空格抓手态下 dragRef 属于舞台的 pan 拖动，不能在这里提前清掉
    if (spaceDownRef.current) return;
    const dr = dragRef.current;
    if (!dr || dr.mode !== 'balloon') { dragRef.current = null; return; }
    const dc = dragCenter;
    if (dc && dr.moved &&
        (Math.abs(dc.x - dr.startCenterX) > 0.5 || Math.abs(dc.y - dr.startCenterY) > 0.5)) {
      const anchor = layoutMap.get(dr.pk!)?.anchor ?? null;
      const leader: number[][] = anchor
        ? [[anchor.x, anchor.y], [dc.x, dc.y]]
        : [[dc.x, dc.y]];
      saveMut.mutate({
        featurePk: dr.pk!,
        center_x: Math.round(dc.x * 100) / 100,
        center_y: Math.round(dc.y * 100) / 100,
        leader_points: leader,
      });
    }
    dragRef.current = null;
    setDragPk(null);
    setDragCenter(null);
    // 让本轮 setDragPk(null) 触发的 effect 先跑完（dragPk=null 时仍会被 suppressLocateRef 挡住），
    // 然后下一轮"用户主动点击别的气球/点表格行"时不再被抑制。
    // 用 setTimeout(0) 而非微任务，确保 effect 已 flush。
    setTimeout(() => { suppressLocateRef.current = false; }, 0);
  };

  // 点击气球（无拖动）
  const handleBalloonClick = (f: Feature, e: React.MouseEvent) => {
    e.stopPropagation();
    if (spaceDownRef.current) return;   // 空格抓手态：拖动/点击只用于平移，不切换选中
    const dr = dragRef.current;
    if (dr?.moved) return; // 若是拖完，忽略 click
    selectFeature(f.id);
    onFeatureClick?.(f);
  };

  // 引线折点（世界 PNG 坐标）
  const leaderPts = (f: Feature): { x: number; y: number }[] => {
    const lp = layoutMap.get(f.id);
    const anchor = lp?.anchor ?? { x: f.x, y: f.y };
    const c = balloonCenterPt(f);
    if (dragPk === f.id) return [anchor, c];
    if (lp?.leader?.length && lp.leader.length >= 2) {
      return lp.leader.map(p => ({ x: p[0], y: p[1] }));
    }
    return [anchor, c];
  };

  // PNG 像素 → 世界(PDF 点)换算（svg overlay 坐标轴用 PDF 点）

  // 控制栏
  const zoomPct = Math.round(view.zoom * 100);

  // 光标：空格抓手 > 平移中 > 框选十字 > 默认抓手（读 dragRef 不触发渲染，但
  // 拖拽过程 setView / 空格按下 setSpaceDown 都会重渲染，足够即时）
  const panning = dragRef.current?.mode === 'pan';
  const cursorStyle = spaceDown
    ? (panning ? 'grabbing' : 'grab')
    : regionSelectMode ? 'crosshair'
    : panning ? 'grabbing' : 'grab';

  // ---------- 框选区域：页面坐标 → 屏幕像素（随缩放平移自动跟随） ----------
  const toScreen = (r: RegionRect) => {
    const perPx = unitToPx * view.zoom;
    const ax = r.x0 * perPx + view.tx, ay = r.y0 * perPx + view.ty;
    const bx = r.x1 * perPx + view.tx, by = r.y1 * perPx + view.ty;
    return { left: Math.min(ax, bx), top: Math.min(ay, by),
             width: Math.abs(bx - ax), height: Math.abs(by - ay) };
  };
  const activeRegion = regionDraft ?? selRegion;
  const activeRect = activeRegion ? toScreen(activeRegion) : null;
  const regionSizeText = activeRegion
    ? `${Math.round(Math.abs(activeRegion.x1 - activeRegion.x0))} × ${Math.round(Math.abs(activeRegion.y1 - activeRegion.y0))}`
    : '';

  // v0.24：缩放操作抽为回调，供画布底部「悬浮缩放条」复用（以舞台中心为锚点）
  const zoomBy = useCallback((factor: number) => {
    fitAppliedRef.current = false;
    lastAppliedPkRef.current = null;
    setView((v) => {
      const z = Math.min(Math.max(v.zoom * factor, ZOOM_MIN), ZOOM_MAX);
      return {
        ...v, zoom: z,
        tx: stageSize.w / 2 - ((stageSize.w / 2 - v.tx) / v.zoom) * z,
        ty: stageSize.h / 2 - ((stageSize.h / 2 - v.ty) / v.zoom) * z,
      };
    });
  }, [stageSize.w, stageSize.h]);
  const fitWidth = useCallback(() => { fitView('width'); fitAppliedRef.current = true; }, [fitView]);
  const fitFull = useCallback(() => { fitView('full'); fitAppliedRef.current = true; }, [fitView]);
  const zoomActual = useCallback(() => {
    fitAppliedRef.current = false;
    lastAppliedPkRef.current = null;
    setView({ zoom: 1, tx: 0, ty: 0 });
  }, []);

  return (
    <div className="canvas-wrap">
      <div className="canvas-title">
        <span className="canvas-name">
          图纸画布 · 第 {pageIndex + 1} 页
          {dragPk != null && <em className="drag-hint"> 拖动气球中（松手保存）</em>}
          {dragRef.current?.mode === 'pan' && <em className="drag-hint"> 平移中</em>}
          {spaceDown && <em className="drag-hint space-hint"> ⌨ 空格抓手 · 拖拽平移图纸</em>}
          {deleteTarget != null && !ro && (
            <em className="drag-hint">⌨ 按 Delete 键删除 #{deleteTarget.balloon_no}（Ctrl+Z 撤销）</em>
          )}
        </span>
        <span className="canvas-meta"
              title={sharpDpi
                ? `已叠高清局部层：${sharpDpi} DPI（当前视口区域单独重渲染）`
                : '整页底图分辨率'}>
          {natReady
            ? `${Math.round(pngW)}×${Math.round(pngH)} px` +
              (sharpDpi ? ` · 视口 ${sharpDpi} DPI` : '')
            : `${widthPt.toFixed(0)}×${heightPt.toFixed(0)} pt · ${dpi} DPI`}
        </span>
      </div>

      {/* 视口控制栏 */}
      <div className="canvas-toolbar" ref={containerRef}>
        {/* 添加气泡的两个互斥开关：自动识别（动作） / 手动添加（模式）。
            v0.47：三个按钮全是写操作 ⇒ 只读角色整组不渲染（样式面板留着，
            因为那是本地显示偏好，不写库）。 */}
        {!ro && (
        <span className="add-group">
          <button className={`btn sm ${balloonAddMode ? '' : autoBusy ? 'busy' : ''}`}
                  title="自动识别：对当前图纸重新 AI 分析并自动生成气泡（会重建本图，覆盖手动添加）"
                  disabled={balloonAddMode || autoBusy}
                  onClick={runAutoRecognize}>
            {autoBusy ? '⏳ 识别中…' : '◎ 自动识别'}
          </button>
          <button className={`btn sm ${balloonAddMode ? 'primary' : ''}`}
                  title="手动添加：开启后在图纸空白处点击放置气泡；再点一次退出"
                  disabled={autoBusy}
                  onClick={toggleAddMode}>
            {balloonAddMode ? `✎ 手动添加中 (${addCount})` : '✎ 手动添加'}
          </button>
          <button className={`btn sm ${regionSelectMode ? 'primary' : ''}`}
                  title={'框选识别：开启后在图纸上拖拽框选一块区域，松手即自动识别该区域内的尺寸/公差并【追加】气泡。\n'
                       + '与「自动识别」不同：不会清空重建，已确认状态 / 实测数据 / 已拖好的气球全部保留。'}
                  disabled={autoBusy || balloonAddMode}
                  onClick={toggleRegionMode}>
            {regionBusy ? '⏳ 识别中…' : regionSelectMode ? '⬚ 框选识别（拖拽框选）' : '⬚ 框选识别'}
          </button>
        </span>
        )}
        <BalloonStylePanel style={bs} onChange={changeBs} onReset={resetBs} own={bsOwn} />
        {/* v0.56：紧贴「样式」右侧的插槽（当前放「导出气泡图」）。
            ⚠️ 必须紧跟 `BalloonStylePanel` —— 用户要的就是「放到样式旁边」，
               挪到下面任何一行都不再是"旁边"。 */}
        {toolbarAfterStyle}
        {(balloonsQ.data?.overlaps?.length ?? 0) > 0 && (
          <span className="btn sm" style={{
            color: '#b91c1c', borderColor: '#fca5a5', background: '#fef2f2',
            cursor: 'default', fontWeight: 600,
          }} title={`以下气泡的圆面可能压住其他特征的尺寸文字：${
            balloonsQ.data!.overlaps!.map(o => `#${o.balloon_no}`).join('、')
          }。把它们拖到空白处即可 —— 画布所见与导出 PDF 完全一致。`}>
            ⚠ {balloonsQ.data!.overlaps!.length} 个气泡疑似遮挡尺寸
          </span>
        )}
        {toolbarExtra}
        <span className="canvas-tip">
          {ro ? (
            <><i>只读角色</i> · <i>滚轮缩放</i> · <i>空白拖拽平移</i> · <i>长按空格拖拽平移</i> · <i>点气球联动表格</i></>
          ) : regionSelectMode ? (
            <><i>拖拽框选区域</i> · <i>松手即自动识别</i> · <i>长按空格拖拽平移</i> · <i>再点按钮退出</i></>
          ) : (
            <><i>滚轮缩放</i> · <i>空白拖拽平移</i> · <i>长按空格拖拽平移</i> · <i>拖气球移动标注</i> · <i>选中后 Delete 删除</i> · <i>Ctrl+Z 撤销</i></>
          )}
        </span>
      </div>

      {/* 可视舞台：内部世界随 view 平移缩放 */}
      <div className={`canvas-stage viewport ${balloonAddMode ? 'add-mode' : ''} ${regionSelectMode ? 'region-mode' : ''} ${spaceDown ? 'space-pan' : ''}`}
           ref={stageRef}
           onDoubleClick={regionSelectMode ? undefined : onDoubleClick}
           onPointerDown={onStagePointerDown}
           onPointerMove={onStagePointerMove}
           onPointerUp={onStagePointerUp}
           style={{ cursor: cursorStyle }}>
        <img className="canvas-img"
             key={`${drawingId}-${pageIndex}-${imageVersion || '0'}`}
             src={`/api/drawing/${drawingId}/page/${pageIndex}/png?dpi=${dpi}&v=${imageVersion || '0'}`}
             alt={`drawing-${drawingId}-p${pageIndex}`}
             draggable={false}
             style={{
               position: 'absolute', left: view.tx, top: view.ty,
               width: pngW * view.zoom, height: pngH * view.zoom,
               display: imgLoaded ? 'block' : 'none',
               transformOrigin: '0 0',
             }}
             onLoad={(e) => {
               const el = e.currentTarget;
               // 以真实 PNG 尺寸为世界坐标基准，保证 img 与 overlay 1:1 对齐不模糊
               if (el.naturalWidth > 0 && el.naturalHeight > 0) {
                 setNatImg({ w: el.naturalWidth, h: el.naturalHeight });
               }
               setImgLoaded(true);
             }} />
        {!imgLoaded && <div className="canvas-loading">加载图纸…</div>}

        {/* v0.78 高清局部层：屏幕绝对定位，只盖住当前视口，不影响世界坐标系 */}
        {imgLoaded && sharp && (() => {
          const spp = view.zoom * unitToPx;          // 屏像素 / pt
          return (
            <img className="canvas-img canvas-sharp"
                 key={sharp.url}
                 src={sharp.url}
                 alt="sharp"
                 draggable={false}
                 style={{
                   position: 'absolute',
                   left: view.tx + sharp.x0 * spp,
                   top: view.ty + sharp.y0 * spp,
                   width: (sharp.x1 - sharp.x0) * spp,
                   height: (sharp.y1 - sharp.y0) * spp,
                   display: 'block',
                   pointerEvents: 'none',
                 }} />
          );
        })()}

        {imgLoaded && (
          <svg className="canvas-overlay"
               width="100%" height="100%"
               style={{ position: 'absolute', inset: 0 }}>
            <g transform={`translate(${view.tx},${view.ty}) scale(${view.zoom})`}>
              {pageFeatures.map(f => {
                if (f.x == null || f.y == null) return null;
                const anchor = layoutMap.get(f.id)?.anchor ?? { x: f.x, y: f.y };
                const c = balloonCenterPt(f);
                // PDF 点 → PNG 像素（世界坐标，g 已含 view 缩放平移）
                const ax = anchor.x * unitToPx;
                const ay = anchor.y * unitToPx;
                const cx = c.x * unitToPx;
                const cy = c.y * unitToPx;
                const effR = Math.max(r * bs.scale, 0.5);  // 气泡半径（PDF 点，× 样式大小系数）
                const rrPx = Math.max(effR * unitToPx * view.zoom, MIN_BALLOON_PX); // 视觉半径（屏像素）
                const rr = rrPx / view.zoom;               // 世界坐标半径
                const isSel = selectedFeaturePk === f.id;
                // 候选过滤状态：REVIEW（待人工复核）**只由虚线表示**（v0.59）。
                // 旧写法 `review ? '#f9a825' : bs.color` 把描边、引线、描点、序号
                // 一律刷成琥珀 —— 而 `AI_FOUND` 的状态色本来就是同一个琥珀，
                // 两者在画布上无从区分；再加上「摘掉复核标记」这件事以前根本办不到，
                // 于是"改了没反应"里有一半是画布这边贡献的。现在颜色留给状态，
                // 虚线与实线才是复核标记的唯一视觉维度（与表格徽标同一套语言）。
                const fs = (f.filter_status ?? 'ACCEPT') as string;
                const review = fs === 'REVIEW';
                const color = bs.color;                       // 气泡圈+序号主色
                // 描边线宽：正常尺寸下就是样式面板的值；半径很小时按比例收细，免得糊成一团
                const lwScreen = isSel ? bs.lineWidth * 1.8 : bs.lineWidth;
                const lw = Math.min(lwScreen, rrPx * 0.42) / view.zoom;
                const statusColor = STATUS_COLORS[f.status as FeatureStatus] || '#9e9e9e';
                const rejected = f.status === 'REJECTED';
                const filterReason = f.filter_reason ? ` ｜ 待复核:${f.filter_reason}` : ' ｜ 待复核';
                const leader = leaderPts(f).map(p => ({
                  x: p.x * unitToPx, y: p.y * unitToPx,
                }));
                // 编号字号：正常尺寸沿用「半径×1.1、下限 11 屏px」（与旧行为逐像素一致）；
                // 圆变小时改为按半径等比缩（上限 rrPx*1.55 保证圆装不下时不再硬撑），
                // 否则会出现「圈小了、数字还是 11px 撑破圆」。
                const fontPx = Math.min(Math.max(rrPx * BALLOON_FONT_K, BALLOON_FONT_MIN_PX),
                                        rrPx * 1.55);
                const fontSize = (fontPx / view.zoom) * bs.fontScale;
                return (
                  <g key={f.id}
                     className={`balloon ${isSel ? 'selected' : ''} ${rejected ? 'rejected' : ''} ${review ? 'review' : ''}`}
                     onClick={(e) => handleBalloonClick(f, e)}
                     onPointerDown={(e) => onBalloonPointerDown(f, e)}
                     onPointerMove={(e) => onBalloonPointerMove(f, e)}
                     onPointerUp={onBalloonPointerUp}
                     style={{ cursor: isSel ? 'grabbing' : 'grab' }}>
                    <title>{`#${f.balloon_no} · ${f.feature_id}${f.kind === 'gdt' ? ' · GD&T' : ''}${review ? filterReason : ''}（拖拽移动）`}</title>
                    {bs.showLeader && leader.length >= 2 && (
                      <polyline
                        points={leader.map(p => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke={color}
                        strokeWidth={Math.max(lw * 0.5, 0.5 / view.zoom)}
                        strokeDasharray={rejected || review ? '3 3' : undefined}
                        opacity={0.75} />
                    )}
                    {/* 描点：引线指向特征锚点处的端点标记（样式开关） */}
                    {bs.showAnchor && leader.length >= 2 && (
                      <circle cx={leader[0].x} cy={leader[0].y}
                              r={Math.max(effR * 0.14 * unitToPx, 1.0 / view.zoom)}
                              fill={color} stroke="none" opacity={0.9} />
                    )}
                    <circle cx={cx} cy={cy} r={rr}
                            fill={statusColor} fillOpacity={0.14}
                            stroke={color}
                            strokeWidth={lw}
                            strokeDasharray={review ? '4 3' : undefined}
                            style={{
                              filter: isSel ? `drop-shadow(0 0 5px ${color})` : undefined,
                              touchAction: 'none',
                            }} />
                    <text x={cx} y={cy}
                          textAnchor="middle" dominantBaseline="central"
                          fontSize={fontSize} fontWeight="600" fill={color}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      {/* X 修复（2026-10-29）：REVIEW（待人工复核）特征
                          balloon_no=0，圈内不画「0」，只保留虚线待复核视觉；
                          选中时下方仍显示 feature_id 便于定位。 */}
                      {f.balloon_no ? f.balloon_no : ''}
                    </text>
                    {isSel && (
                      <text x={cx} y={cy - rr - (6 / view.zoom)}
                            textAnchor="middle" fontSize={10 / view.zoom} fill="#555"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        {f.feature_id}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Task #55：疑似漏检标记（红，叠在特征气球之上，可点击定位） */}
              {missedOnPage.map((mi) => {
                const gi = coverage!.items.indexOf(mi);     // 全局序号
                const mx = mi.x * unitToPx;
                const my = mi.y * unitToPx;
                const rr = Math.max(r * unitToPx * view.zoom, 9 / view.zoom);
                const isLoc = locatedMissed != null &&
                              locatedMissed.page_no === mi.page_no &&
                              locatedMissed.x === mi.x && locatedMissed.y === mi.y;
                const color = mi.severity === 'POSSIBLE_MISSED' ? '#e53935' : '#f9a825';
                const onLocate = () => setLocatedMissed({
                  idx: gi, x: mi.x, y: mi.y, page_no: mi.page_no,
                  text: mi.text, category_guess: mi.category_guess,
                  severity: mi.severity, confidence: mi.confidence, reason: mi.reason,
                });
                return (
                  <g key={`missed-${gi}`}
                     className={`missed-marker ${isLoc ? 'selected' : ''}`}
                     onClick={(e) => { e.stopPropagation(); onLocate(); }}
                     style={{ cursor: 'pointer' }}>
                    <title>{`${mi.severity === 'POSSIBLE_MISSED' ? '疑似漏检' : '低置信候选'} · ${mi.text}\n分类: ${mi.category_guess} · 置信度 ${mi.confidence}\n${mi.reason}`}</title>
                    <circle cx={mx} cy={my} r={rr}
                            fill="rgba(229,57,53,0.12)"
                            stroke={color}
                            strokeWidth={(isLoc ? 3.2 : 1.8) / view.zoom} />
                    <line x1={mx - rr} y1={my} x2={mx + rr} y2={my}
                          stroke={color} strokeWidth={1.2 / view.zoom} opacity={0.9} />
                    <line x1={mx} y1={my - rr} x2={mx} y2={my + rr}
                          stroke={color} strokeWidth={1.2 / view.zoom} opacity={0.9} />
                    <text x={mx} y={my}
                          textAnchor="middle" dominantBaseline="central"
                          fontSize={Math.max(rr * view.zoom * 1.0 / view.zoom, 11 / view.zoom)}
                          fontWeight="700" fill={color}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      {mi.severity === 'POSSIBLE_MISSED' ? '!' : '?'}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        )}

        {/* ---------- 框选识别：选区矩形 + 确认浮条 ---------- */}
        {regionSelectMode && !activeRegion && (
          <div className="region-hint">拖拽框选要识别的区域 —— 松手即自动识别</div>
        )}
        {regionSelectMode && activeRect && (
          <div className="region-rect"
               style={{ left: activeRect.left, top: activeRect.top,
                        width: activeRect.width, height: activeRect.height }} />
        )}
        {regionSelectMode && regionBusy && activeRect && (
          <div className="region-confirm-bar"
               style={{
                 left: Math.max(4, activeRect.left),
                 top: Math.max(4, activeRect.top - 44),
               }}>
            <span className="rcb-info">选区 {regionSizeText}</span>
            <span className="rcb-info busy">⏳ 正在识别该区域…</span>
          </div>
        )}
      </div>

      {/* v0.24：悬浮缩放条 —— 浮于画布底部居中，把工具条高度让给识别/过滤信息 */}
      <div className="floating-zoom-bar">
        <button className="fz-btn" title="缩小（滚轮向下）" onClick={() => zoomBy(1 / 1.25)}>－</button>
        <span className="fz-pct">{zoomPct}%</span>
        <button className="fz-btn" title="放大（滚轮向上）" onClick={() => zoomBy(1.25)}>＋</button>
        <span className="fz-sep" />
        <button className="fz-btn wide" title="适应宽度（等比缩放到画布满宽）" onClick={fitWidth}>适应宽</button>
        <button className="fz-btn wide" title="适配整页" onClick={fitFull}>整页</button>
        <button className="fz-btn wide" title="实际大小 100%" onClick={zoomActual}>100%</button>
      </div>
    </div>
  );
}
